// =============================================================================
// ContactsImport.jsx — Unleashed Customer-Contacts CSV import (Phase 4b)
// =============================================================================
// Bulk-creates caseworker accounts from the Unleashed Contacts export —
// SILENTLY (John, 5 July 2026): random password, send_welcome:false, NO email
// at creation. Invitations go out later via the Phase-4c go-live utility.
//
// All decisions live in the PURE planner (src/services/contactsImport.ts):
// the preview IS the dry run — nothing writes until Execute. Idempotent:
// re-running a file plans attach-only/nothing for people who already exist,
// so a partially-failed run is fixed by simply running the same file again.
//
// Each planned account goes through the SAME chain the single-user form uses:
// admin-users Edge Function `create` (service-role auth account + profile) →
// upsertProfile safety net → upsertUserShelter membership rows (user-axis
// is_primary + shelter-axis is_primary_user, db-41).

import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { listShelterProfiles, listStaffProfiles, upsertProfile } from "../../data/profiles";
import { listAllShelters } from "../../data/shelters";
import {
  insertContactMembership,
  listAllUserShelters,
  setPrimaryContactMembership,
  setPrimaryShelterUser,
  upsertUserShelter,
} from "../../data/userShelters";
import {
  generateImportPassword,
  parseContactsCsv,
  planContactsImport,
} from "../../services/contactsImport";
import { logAudit } from "../../utils/auditLog";
import { GLYPH } from "../../utils/glyphs";
import { logger } from "../../utils/logger";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn, Card } from "../../utils/ui.jsx";

const EDGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-users`;

// The contacts import now stores raw names and parks the role suffix in
// contact_label. This builds a worklist of every contact-to-create that carries
// such an imported role — the list to hand-tidy in the portal after GUID linking
// (cleaning the name there flows back to Unleashed). Pure + exported for testing.
export function buildRoleWorklistRows(plan) {
  const rows = [];
  for (const a of plan.accounts || []) {
    if (!a.contact_label) continue;
    const name = `${a.first_name || ""} ${a.last_name || ""}`.trim();
    for (const m of a.memberships || []) {
      rows.push({
        shelter_code: m.shelter_code,
        name,
        email: a.email || "",
        role: a.contact_label,
      });
    }
  }
  for (const c of [...(plan.noLoginContacts || []), ...(plan.sameLoginContacts || [])]) {
    if (!c.contact_label) continue;
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    rows.push({ shelter_code: c.shelter_code, name, email: c.email || "", role: c.contact_label });
  }
  return rows;
}

export function roleWorklistCsv(rows) {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ["DFV Service", "Name", "Email", "Imported Role"];
  return [
    header.join(","),
    ...rows.map((r) => [r.shelter_code, r.name, r.email, r.role].map(esc).join(",")),
  ].join("\n");
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ContactsImport() {
  const { user } = useSession();
  const [step, setStep] = React.useState("upload"); // upload | preview | running | done
  const [err, setErr] = React.useState("");
  const [plan, setPlan] = React.useState(null);
  const [shelterNames, setShelterNames] = React.useState({});
  // Identity Phase 2: membership upserts key on shelter_id — resolve each code to
  // its shelter_id from the loaded shelters list (shelter_code stays for display).
  const [shelterIdByCode, setShelterIdByCode] = React.useState({});
  const [progress, setProgress] = React.useState({ done: 0, total: 0 });
  const [failures, setFailures] = React.useState([]);
  const [result, setResult] = React.useState(null);

  function reset() {
    setStep("upload");
    setErr("");
    setPlan(null);
    setFailures([]);
    setResult(null);
    setProgress({ done: 0, total: 0 });
  }

  function handleFile(e) {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async function (ev) {
      const { rows, error } = parseContactsCsv(String(ev.target.result));
      if (error) {
        setErr(error);
        return;
      }
      const shelters = await listAllShelters();
      const caseworkers = await listShelterProfiles();
      const staff = await listStaffProfiles();
      const memberships = await listAllUserShelters();
      if (!shelters || !caseworkers || !staff || memberships === null) {
        setErr("Couldn't load the portal's current users/shelters — try again.");
        return;
      }
      const names = {};
      const idByCode = {};
      const withPrimaryContact = [];
      shelters.forEach(function (s) {
        names[s.shelter_code] = s.shelter_name;
        if (s.shelter_code && s.id) idByCode[s.shelter_code] = s.id;
        // Contact-model Phase 3: a shelter with shelters.primary_contact already set
        // has a Primary contact — the import must not assign a second (at most one).
        if (s.primary_contact && s.shelter_code) withPrimaryContact.push(s.shelter_code);
      });
      setShelterNames(names);
      setShelterIdByCode(idByCode);
      setPlan(
        planContactsImport(
          rows,
          shelters.map(function (s) {
            return s.shelter_code;
          }),
          caseworkers.concat(staff),
          memberships,
          withPrimaryContact
        )
      );
      setStep("preview");
    };
    reader.readAsText(file);
  }

  async function createOneAccount(account, token) {
    const resp = await fetch(EDGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        action: "create",
        email: account.email,
        password: generateImportPassword(),
        display_name: account.display_name,
        shelter_name: shelterNames[account.primary_shelter_code] || "",
        shelter_code: account.primary_shelter_code,
        role: "shelter",
        state: "",
        first_name: account.first_name,
        last_name: account.last_name,
        is_generic: account.is_generic,
        login_method: "password",
        // John's call (5 July 2026): SILENT creation — the go-live invite
        // utility (Phase 4c) sends the emails later.
        send_welcome: false,
      }),
    });
    const res = await resp.json();
    if (res.error) throw new Error(res.error);
    const userId = res.user_id || res.user?.id;
    if (!userId) throw new Error("Edge function returned no user id");
    const profUp = await upsertProfile({
      id: userId,
      display_name: account.display_name,
      // THE WALL (db-142): a caseworker profile carries LOGIN MECHANICS ONLY. The
      // contact's name / email / phone belong on the user_shelters membership
      // (seeded below), NOT on profiles.staff_* (those are Zephyr-staff-only — the
      // db-142 trigger REJECTS any staff_* on a role='shelter' row). This safety-net
      // re-upsert only re-asserts the login-mechanics fields the EF already set; it
      // must NEVER write staff_*.  (Removed the staff_* write 2026-08-10 — it was a
      // pre-wall Phase-3 leftover that broke the whole contacts import.)
      role: "shelter",
      // No profiles.shelter_code (Phase 4 B4-C) — the membership loop below sets
      // user_shelters.is_primary, the source of truth for the primary shelter.
      is_generic: account.is_generic,
      login_method: "password",
    });
    if (profUp.error) throw new Error(`profile: ${profUp.error.message}`);
    // Contact-model Phase 3 (Option A): split the imported phones into the two fixed
    // contact fields (04… / Mobile-labelled → Mobile; else Phone) and seed the
    // per-service contact from the imported account details.
    const digits = (n) => (n || "").replace(/\D/g, "");
    const mobileEntry = (account.phones || []).find(
      (p) => digits(p.number).startsWith("04") || /mobile/i.test(p.label || "")
    );
    const phoneEntry = (account.phones || []).find((p) => p !== mobileEntry && p.number);
    const contactMobile = mobileEntry ? mobileEntry.number : null;
    const contactPhone = phoneEntry ? phoneEntry.number : null;
    for (const m of account.memberships) {
      const shelterId = shelterIdByCode[m.shelter_code];
      if (!shelterId) throw new Error(`membership ${m.shelter_code}: DFV Service not found`);
      const usRes = await upsertUserShelter({
        user_id: userId,
        shelter_id: shelterId,
        shelter_code: m.shelter_code,
        is_primary: m.is_primary,
        first_name: account.first_name || null,
        last_name: account.last_name || null,
        email: account.email || null,
        phone: contactPhone,
        mobile: contactMobile,
        // The Role parsed out of the name suffix ("Jane Smith (Manager)") — the planner
        // puts it on account.contact_label and the contact-ONLY branch below already
        // writes it. This path hard-coded null, so anyone who got a login lost their
        // Role, contradicting docs/contacts-import-collision-handling-plan.md.
        // Finding B10, 18 Aug 2026.
        contact_label: account.contact_label || null,
      });
      if (usRes.error) throw new Error(`membership ${m.shelter_code}: ${usRes.error.message}`);
      // Pointer-only (contact-model Phase 2): if this membership is the shelter's
      // Primary contact, point shelters.primary_contact at it (best-effort).
      if (m.is_primary_user) {
        const pRes = await setPrimaryShelterUser(shelterId, userId);
        if (pRes.error)
          logger.warn(`Contacts import: primary set ${m.shelter_code}:`, pRes.error.message);
      }
    }
  }

  async function runImport() {
    if (!plan) return;
    setStep("running");
    const contactOnly = [...plan.noLoginContacts, ...plan.sameLoginContacts];
    const total = plan.accounts.length + plan.attaches.length + contactOnly.length;
    setProgress({ done: 0, total: total });
    const fails = [];
    let created = 0;
    let attached = 0;
    let contacts = 0;
    const sessionRes = await getSupabaseClient().auth.getSession();
    const token = sessionRes.data?.session ? sessionRes.data.session.access_token : "";
    let done = 0;
    for (const account of plan.accounts) {
      try {
        await createOneAccount(account, token);
        created += 1;
      } catch (e) {
        logger.warn("Contacts import: create failed:", account.email, e.message);
        fails.push({ email: account.email, stage: "create", message: e.message });
      }
      done += 1;
      setProgress({ done: done, total: total });
    }
    for (const at of plan.attaches) {
      const shelterId = shelterIdByCode[at.shelter_code];
      if (!shelterId) {
        const msg = `DFV Service ${at.shelter_code} not found`;
        logger.warn("Contacts import: attach failed:", at.email, msg);
        fails.push({ email: at.email, stage: "attach", message: msg });
        done += 1;
        setProgress({ done: done, total: total });
        continue;
      }
      const usRes = await upsertUserShelter({
        user_id: at.user_id,
        shelter_id: shelterId,
        shelter_code: at.shelter_code,
        is_primary: false,
      });
      if (usRes.error) {
        logger.warn("Contacts import: attach failed:", at.email, usRes.error.message);
        fails.push({ email: at.email, stage: "attach", message: usRes.error.message });
      } else {
        // Pointer-only (contact-model Phase 2): mark this membership as the
        // shelter's Primary contact when the plan says so (best-effort).
        if (at.is_primary_user) {
          const pRes = await setPrimaryShelterUser(shelterId, at.user_id);
          if (pRes.error)
            logger.warn(
              "Contacts import: primary set failed:",
              at.shelter_code,
              pRes.error.message
            );
        }
        attached += 1;
      }
      done += 1;
      setProgress({ done: done, total: total });
    }
    // Contact-only memberships (no-login + Same-Login) — user_id NULL rows, applied
    // as plain inserts; when a contact is the shelter's Primary, point
    // shelters.primary_contact at the new membership id (works for login-less contacts).
    for (const c of contactOnly) {
      const shelterId = shelterIdByCode[c.shelter_code];
      const label = `${c.first_name} ${c.last_name}`.trim() || c.shelter_code;
      if (!shelterId) {
        const msg = `DFV Service ${c.shelter_code} not found`;
        logger.warn("Contacts import: contact failed:", label, msg);
        fails.push({ email: label, stage: "contact", message: msg });
        done += 1;
        setProgress({ done: done, total: total });
        continue;
      }
      const ins = await insertContactMembership({
        user_id: null,
        shelter_id: shelterId,
        shelter_code: c.shelter_code,
        is_primary: false,
        first_name: c.first_name || null,
        last_name: c.last_name || null,
        email: c.email,
        phone: c.phone || null,
        mobile: c.mobile || null,
        contact_label: c.contact_label || null,
      });
      if (ins.error) {
        logger.warn("Contacts import: contact failed:", label, ins.error.message);
        fails.push({ email: label, stage: "contact", message: ins.error.message });
      } else {
        contacts += 1;
        if (c.is_primary_user && ins.id) {
          const pRes = await setPrimaryContactMembership(shelterId, ins.id);
          if (pRes.error)
            logger.warn("Contacts import: primary set failed:", c.shelter_code, pRes.error.message);
        }
      }
      done += 1;
      setProgress({ done: done, total: total });
    }
    logAudit("users_imported", {
      created: created,
      attached: attached,
      contacts: contacts,
      failed: fails.length,
      by: user?.email || "",
    });
    setFailures(fails);
    setResult({ created: created, attached: attached, contacts: contacts });
    setStep("done");
  }

  return (
    <Card style={{ padding: 24, marginTop: 24 }}>
      <div className="text-[15px] font-bold text-zephyr-teal-dark mb-1">
        Step 3 — Import Users (Customer Contacts)
      </div>
      <div className="text-[13px] text-zephyr-muted mb-4">
        Upload the Unleashed <b>Customer Contacts</b> export. Accounts are created <b>silently</b> —
        no emails are sent; invitations go out via the go-live invite utility. Safe to re-run:
        existing people are attached, never duplicated.
      </div>

      {step === "upload" && (
        <div>
          {/* Native file inputs can't be styled — hidden input + a link-styled
              label (bold + underlined so it's obviously clickable, John). */}
          <label
            htmlFor="contacts-import-file"
            className="cursor-pointer font-bold underline text-zephyr-teal-dark text-[13px]"
          >
            📄 Choose the Customer Contacts CSV…
          </label>
          <input
            id="contacts-import-file"
            type="file"
            accept=".csv,.txt"
            aria-label="Customer contacts CSV file"
            onChange={handleFile}
            className="hidden"
          />
          {err && <div className="text-zephyr-red mt-3 text-[13px]">{err}</div>}
        </div>
      )}

      {step === "preview" && plan && (
        <div>
          <div className="flex gap-6 text-[13px] flex-wrap mb-3">
            <span>
              <strong style={{ color: "#2E7D32" }}>{plan.accounts.length}</strong> accounts to
              create (silent)
            </span>
            <span>
              <strong style={{ color: "#1565C0" }}>{plan.attaches.length}</strong> memberships to
              attach
            </span>
            <span>
              <strong style={{ color: "#7B5EA7" }}>{plan.noLoginContacts.length}</strong> no-login
              contacts
            </span>
            <span>
              <strong style={{ color: "#7B5EA7" }}>{plan.sameLoginContacts.length}</strong>{" "}
              shared-login contacts
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.alreadyComplete}</strong> already complete
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.skippedInternal}</strong> internal skipped
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.skippedNoEmailNoName}</strong> empty rows
              skipped
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.skippedPlaceholder}</strong> placeholders
              skipped
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.skippedStaffEmail}</strong> Zephyr staff
              skipped
            </span>
            <span>
              <strong style={{ color: "#607D79" }}>{plan.skippedInvalidEmail}</strong> invalid email
              skipped
            </span>
          </div>
          {(() => {
            const roleRows = buildRoleWorklistRows(plan);
            return roleRows.length > 0 ? (
              <div className="mb-3">
                <button
                  type="button"
                  onClick={() =>
                    downloadCsv("contact-role-worklist.csv", roleWorklistCsv(roleRows))
                  }
                  className="text-[13px] font-semibold text-zephyr-teal underline cursor-pointer bg-transparent border-none p-0"
                >
                  📄 Download role worklist ({roleRows.length})
                </button>
                <span className="text-[12px] text-zephyr-muted ml-2">
                  — contacts carrying an imported role; tidy these in the portal after GUID linking
                  (edits flow back to Unleashed).
                </span>
              </div>
            ) : null;
          })()}
          {plan.staffConflicts.length > 0 && (
            <div className="text-[13px] mb-2" style={{ color: "#B8860B" }}>
              ⚠️ {plan.skippedStaffEmail} row(s) NOT imported — these addresses are Zephyr staff
              logins, and a Zephyr person can never be a DFV Service contact. Fix the contact record
              in Unleashed: {plan.staffConflicts.join(", ")}
            </div>
          )}
          {plan.invalidEmails.length > 0 && (
            <div className="text-[13px] mb-2" style={{ color: "#B8860B" }}>
              ⚠️ {plan.skippedInvalidEmail} row(s) NOT imported — the email is not a valid address.
              Fix the contact in Unleashed and re-import:{" "}
              {plan.invalidEmails
                .map((x) => `${x.name} (${x.shelter_code}) "${x.email}"`)
                .join(", ")}
            </div>
          )}
          {plan.unknownShelters.length > 0 && (
            <div className="text-[13px] mb-2" style={{ color: "#B8860B" }}>
              ⚠️ Unknown DFV Service code(s) skipped (import the DFV Services first):{" "}
              {plan.unknownShelters.join(", ")}
              {plan.unknownShelterContacts.length > 0 && (
                <> — {plan.unknownShelterContacts.length} contact(s) affected</>
              )}
            </div>
          )}
          {plan.servicesWithoutLogin.length > 0 && (
            <div className="text-[13px] mb-2" style={{ color: "#B8860B" }}>
              ⚠️ {plan.servicesWithoutLogin.length} service(s) will have no login contact yet (add a
              caseworker later): {plan.servicesWithoutLogin.join(", ")}
            </div>
          )}
          {plan.accounts.length > 0 && (
            <div
              className="border border-zephyr-border rounded-[8px] overflow-y-auto mb-4 text-[12px]"
              style={{ maxHeight: 220 }}
            >
              {plan.accounts.slice(0, 50).map(function (a) {
                return (
                  <div
                    key={a.email}
                    className="py-[6px] px-3 border-t border-[#EEF3F2] first:border-t-0"
                  >
                    <b>{a.display_name}</b> ({a.email}) →{" "}
                    {a.memberships
                      .map(function (m) {
                        return m.shelter_code + (m.is_primary_user ? " (Primary)" : "");
                      })
                      .join(", ")}
                    {a.is_generic && (
                      <span className="ml-2 text-[10px] font-bold text-white bg-[#7B5EA7] rounded-[3px] py-[1px] px-[5px]">
                        SHARED
                      </span>
                    )}
                  </div>
                );
              })}
              {plan.accounts.length > 50 && (
                <div className="py-[6px] px-3 text-zephyr-muted">
                  … and {plan.accounts.length - 50} more
                </div>
              )}
            </div>
          )}
          {(plan.sameLoginContacts.length > 0 || plan.noLoginContacts.length > 0) && (
            <div className="mb-4">
              <div className="text-[12px] text-zephyr-muted mb-1">
                Contact-only records (no portal login) — created by the import so no one is dropped.{" "}
                <b>Same Login</b> = a person after the first who shares another person's email (one
                login per email); <b>No Login</b> = a contact with no email. Adjust any of these
                later in the DFV Service&apos;s Users tab if needed.
              </div>
              <div
                className="border border-zephyr-border rounded-[8px] overflow-y-auto text-[12px]"
                style={{ maxHeight: 200 }}
              >
                {plan.sameLoginContacts.slice(0, 60).map(function (c) {
                  return (
                    <div
                      key={`sl-${c.shelter_code}-${c.email}-${c.first_name}-${c.last_name}`}
                      className="py-[6px] px-3 border-t border-[#EEF3F2] first:border-t-0"
                    >
                      <b>{`${c.first_name} ${c.last_name}`.trim() || "(no name)"}</b> @{" "}
                      {c.shelter_code}
                      {c.is_primary_user ? " (Primary)" : ""}
                      <span className="ml-2 text-[10px] font-bold text-white bg-[#B8860B] rounded-[3px] py-[1px] px-[5px]">
                        SAME LOGIN
                      </span>
                      <span className="ml-2 text-zephyr-muted">shares {c.email}</span>
                    </div>
                  );
                })}
                {plan.sameLoginContacts.length > 60 && (
                  <div className="py-[6px] px-3 text-zephyr-muted">
                    … and {plan.sameLoginContacts.length - 60} more Same Login
                  </div>
                )}
                {plan.noLoginContacts.slice(0, 60).map(function (c) {
                  return (
                    <div
                      key={`nl-${c.shelter_code}-${c.first_name}-${c.last_name}`}
                      className="py-[6px] px-3 border-t border-[#EEF3F2] first:border-t-0"
                    >
                      <b>{`${c.first_name} ${c.last_name}`.trim() || "(no name)"}</b> @{" "}
                      {c.shelter_code}
                      {c.is_primary_user ? " (Primary)" : ""}
                      <span className="ml-2 text-[10px] font-bold text-white bg-[#7B5EA7] rounded-[3px] py-[1px] px-[5px]">
                        NO LOGIN
                      </span>
                    </div>
                  );
                })}
                {plan.noLoginContacts.length > 60 && (
                  <div className="py-[6px] px-3 text-zephyr-muted">
                    … and {plan.noLoginContacts.length - 60} more No Login
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-[10px]">
            <Btn variant="secondary" onClick={reset}>
              Cancel
            </Btn>
            <Btn
              onClick={runImport}
              disabled={
                plan.accounts.length === 0 &&
                plan.attaches.length === 0 &&
                plan.noLoginContacts.length + plan.sameLoginContacts.length === 0
              }
            >
              Create {plan.accounts.length} account{plan.accounts.length === 1 ? "" : "s"}
              {plan.attaches.length > 0 ? ` + ${plan.attaches.length} attach(es)` : ""}
              {plan.noLoginContacts.length + plan.sameLoginContacts.length > 0
                ? ` + ${plan.noLoginContacts.length + plan.sameLoginContacts.length} contact${
                    plan.noLoginContacts.length + plan.sameLoginContacts.length === 1 ? "" : "s"
                  }`
                : ""}
            </Btn>
          </div>
        </div>
      )}

      {step === "running" && (
        <div className="text-[13px]">
          <div className="mb-2">
            Creating accounts… {progress.done} / {progress.total} (leave this page open)
          </div>
          <div
            className="h-2 rounded bg-zephyr-off-white overflow-hidden"
            style={{ maxWidth: 420 }}
          >
            <div
              className="h-2 bg-zephyr-teal"
              style={{
                width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div>
          <div className="text-[13px] mb-2">
            <span style={{ color: "#2E7D32", fontWeight: 700 }}>
              {result.created} account{result.created === 1 ? "" : "s"} created {GLYPH.check}
            </span>
            <span className="ml-3" style={{ color: "#1565C0", fontWeight: 600 }}>
              {result.attached} membership{result.attached === 1 ? "" : "s"} attached
            </span>
            <span className="ml-3" style={{ color: "#7B5EA7", fontWeight: 600 }}>
              {result.contacts} contact{result.contacts === 1 ? "" : "s"} added
            </span>
            {failures.length > 0 && (
              <span className="ml-3 text-zephyr-red font-semibold">{failures.length} failed</span>
            )}
          </div>
          {failures.length > 0 && (
            <div
              className="border border-zephyr-border rounded-[8px] overflow-y-auto mb-3 text-[12px]"
              style={{ maxHeight: 160 }}
            >
              {failures.map(function (f) {
                return (
                  <div key={`${f.email}-${f.stage}`} className="py-[5px] px-3">
                    <b>{f.email}</b> ({f.stage}): {f.message}
                  </div>
                );
              })}
            </div>
          )}
          {failures.length > 0 && (
            <div className="text-[12px] text-zephyr-muted mb-3">
              Re-running the same file is safe — it will only retry what's missing.
            </div>
          )}
          <Btn variant="secondary" onClick={reset}>
            Import another file
          </Btn>
        </div>
      )}
    </Card>
  );
}
